import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Button, Modal, Form, Pagination, Row, Col, InputGroup, Table, Spinner, Badge } from 'react-bootstrap';
import { 
  FaTruck, FaUser, FaPhoneAlt, FaEnvelope, FaPlus, 
  FaSearch, FaSyncAlt, FaClipboard, FaCheckCircle, FaTimesCircle, 
  FaEye, FaTimes, FaShieldAlt, FaThLarge, FaList, FaClock, FaLock
} from 'react-icons/fa';
import { get, post } from '../utils/api';
import ProgressDialog from './ProgressDialog';
import { useToast } from './ToastContext';
import NoDataCard from './NoDataCard';
import './AllDeliveryPersons.css';

const AllDeliveryPersons = () => {
  const { showToast } = useToast();
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search, Filter & View Mode
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

  // Modals
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [newDeliveryPerson, setNewDeliveryPerson] = useState({ name: '', email: '', phone: '', password: '' });
  const [formErrors, setFormErrors] = useState({});

  const fetchDeliveryPersons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await get('/delivery/personnel?limit=1000');
      const persons = resp?.data?.data ?? resp?.data ?? [];
      setDeliveryPersons(persons || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch delivery staff.');
      setDeliveryPersons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeliveryPersons();
  }, [fetchDeliveryPersons]);

  const handleShowModal = (deliveryPerson) => {
    setSelectedDeliveryPerson(deliveryPerson);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDeliveryPerson(null);
  };

  const handleCreateDeliveryPerson = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!newDeliveryPerson.name?.trim()) errors.name = 'Full name is required';
    if (!newDeliveryPerson.phone?.trim()) errors.phone = 'Mobile number is required';
    if (!newDeliveryPerson.email?.trim()) errors.email = 'Email address is required';
    if (!newDeliveryPerson.password || newDeliveryPerson.password.length < 6) errors.password = 'Password must be at least 6 characters';

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setCreatingStaff(true);
    try {
      const response = await post('/users/delivery', newDeliveryPerson);
      if (response && response.success) {
        showToast('Delivery courier staff created successfully!', 'success');
        setShowCreateModal(false);
        setNewDeliveryPerson({ name: '', email: '', phone: '', password: '' });
        fetchDeliveryPersons();
      } else {
        showToast(response?.message || 'Failed to create delivery person', 'danger');
      }
    } catch (err) {
      showToast(`Failed to create delivery person: ${err.message}`, 'danger');
    } finally {
      setCreatingStaff(false);
    }
  };

  const handleCopy = async (text, label = 'Value') => {
    try {
      if (!navigator?.clipboard) throw new Error('Clipboard not available');
      await navigator.clipboard.writeText(text || '');
      showToast(`${label} copied to clipboard`, 'success');
    } catch (err) {
      showToast(`Failed to copy ${label}`, 'danger');
    }
  };

  // KPI Metrics
  const metrics = useMemo(() => {
    const total = deliveryPersons.length;
    const active = deliveryPersons.filter(d => d.is_active !== 0 && d.is_active !== false).length;
    return { total, active };
  }, [deliveryPersons]);

  // Filtered Delivery Staff
  const filteredPersons = useMemo(() => {
    return deliveryPersons.filter(p => {
      if (searchTerm.trim() !== '') {
        const q = searchTerm.toLowerCase();
        const matchName = (p.name || '').toLowerCase().includes(q);
        const matchEmail = (p.email || '').toLowerCase().includes(q);
        const matchPhone = (p.phone || p.mobile || '').toLowerCase().includes(q);
        const matchId = String(p.delivery_person_id || p.user_id || '').includes(q);
        if (!matchName && !matchEmail && !matchPhone && !matchId) return false;
      }
      return true;
    });
  }, [deliveryPersons, searchTerm]);

  return (
    <Container fluid className="delivery-persons-page px-3 px-lg-4 py-3">
      <ProgressDialog show={loading && deliveryPersons.length === 0} message="Loading delivery personnel..." />

      {/* Header & KPI Summary */}
      <div className="dp-header-panel mb-4 p-3 p-lg-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="dp-master-icon">
              <FaTruck />
            </div>
            <div>
              <h3 className="dp-title mb-0">Delivery Personnel</h3>
              <p className="dp-subtitle mb-0 text-muted">Manage courier staff, field delivery agents & driver assignments.</p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button 
              variant="primary" 
              size="sm" 
              onClick={() => { setFormErrors({}); setShowCreateModal(true); }}
              className="dp-add-btn d-flex align-items-center gap-2"
            >
              <FaPlus /> Add Delivery Staff
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={fetchDeliveryPersons} 
              className="dp-refresh-btn d-flex align-items-center gap-2"
            >
              <FaSyncAlt className={loading ? 'fa-spin' : ''} /> Refresh
            </Button>
          </div>
        </div>

        {/* KPI Metrics Row */}
        <Row className="g-3">
          <Col xs={6} md={6}>
            <div className="dp-kpi-card">
              <div className="kpi-icon total">
                <FaTruck />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">{metrics.total}</div>
                <div className="kpi-label">Registered Couriers</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={6}>
            <div className="dp-kpi-card">
              <div className="kpi-icon active">
                <FaCheckCircle />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-success">{metrics.active}</div>
                <div className="kpi-label">Active Field Personnel</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Toolbar & Search */}
      <div className="dp-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Search Box */}
          <div className="dp-search-wrapper flex-grow-1">
            <InputGroup className="dp-search-group">
              <InputGroup.Text className="bg-transparent border-0 pe-1 text-muted">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by rider name, phone, email, courier ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="dp-search-input border-0 ps-1"
              />
              {searchTerm && (
                <Button 
                  variant="link" 
                  onClick={() => setSearchTerm('')} 
                  className="dp-clear-search text-muted border-0"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* View Mode Toggle */}
          <div className="dp-view-switch">
            <button 
              type="button" 
              className={`switch-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <FaThLarge />
            </button>
            <button 
              type="button" 
              className={`switch-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <FaList />
            </button>
          </div>
        </div>
      </div>

      {/* Delivery Persons List */}
      {filteredPersons.length === 0 && !loading ? (
        <div className="dp-no-data-box py-5 text-center rounded glassmorphic-panel">
          <NoDataCard 
            title="No Delivery Personnel Found"
            message={searchTerm ? "No riders match your search query." : "No delivery staff registered in system."}
            icon="🚚"
            actionButton={
              searchTerm ? (
                <Button variant="primary" size="sm" onClick={() => setSearchTerm('')}>
                  Clear Search
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                  Add New Staff
                </Button>
              )
            }
          />
        </div>
      ) : viewMode === 'grid' ? (
        <div className="dp-cards-grid">
          {filteredPersons.map((person) => {
            const initial = (person.name || 'D').charAt(0).toUpperCase();

            return (
              <div className="dp-card-item glassmorphic-panel" key={person.delivery_person_id || person.user_id}>
                <div className="dp-card-header p-3 border-bottom d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <div className="dp-avatar-circle">
                      {initial}
                    </div>
                    <div>
                      <h6 className="dp-card-name mb-0">{person.name || 'Unnamed Staff'}</h6>
                      <span className="dp-id-pill">ID #{person.delivery_person_id || person.user_id}</span>
                    </div>
                  </div>

                  <span className="badge bg-success">Active Courier</span>
                </div>

                <div className="dp-card-body p-3">
                  {/* Phone */}
                  <div className="dp-contact-item d-flex justify-content-between align-items-center mb-2">
                    <span className="dp-contact-label">
                      <FaPhoneAlt className="me-2 text-info small" /> Contact
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="dp-contact-val text-white">{person.phone || person.mobile || 'N/A'}</span>
                      {(person.phone || person.mobile) && (
                        <button 
                          type="button" 
                          className="dp-copy-btn" 
                          onClick={() => handleCopy(person.phone || person.mobile, 'Phone number')}
                          title="Copy phone"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="dp-contact-item d-flex justify-content-between align-items-center mb-2">
                    <span className="dp-contact-label">
                      <FaEnvelope className="me-2 text-primary small" /> Email
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="dp-contact-val text-truncate" style={{ maxWidth: '170px' }} title={person.email || 'N/A'}>
                        {person.email || 'N/A'}
                      </span>
                      {person.email && (
                        <button 
                          type="button" 
                          className="dp-copy-btn" 
                          onClick={() => handleCopy(person.email, 'Email')}
                          title="Copy email"
                        >
                          <FaClipboard />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="dp-card-footer p-2 border-top">
                  <Button 
                    variant="outline-info" 
                    size="sm" 
                    className="w-100 dp-view-btn"
                    onClick={() => handleShowModal(person)}
                  >
                    <FaEye className="me-1" /> View Rider Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="dp-table-wrapper rounded glassmorphic-panel overflow-hidden">
          <Table responsive hover className="dp-data-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th>Rider / Courier Name</th>
                <th>Phone Number</th>
                <th>Email Address</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPersons.map((p) => (
                <tr key={p.delivery_person_id || p.user_id}>
                  <td className="text-muted fw-bold">#{p.delivery_person_id || p.user_id}</td>
                  <td>
                    <div className="fw-bold text-white d-flex align-items-center gap-2">
                      <FaUser className="text-info small" /> {p.name || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <span className="text-white">{p.phone || p.mobile || 'N/A'}</span>
                  </td>
                  <td>
                    <span className="text-muted">{p.email || 'N/A'}</span>
                  </td>
                  <td>
                    <span className="badge bg-success">Active</span>
                  </td>
                  <td className="text-end">
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      className="dp-tbl-btn"
                      onClick={() => handleShowModal(p)}
                    >
                      <FaEye />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Courier Inspector Modal */}
      <Modal show={showModal} onHide={handleCloseModal} scrollable className="dp-modal-modern">
        <Modal.Header closeButton className="dp-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="dp-modal-icon">
              <FaTruck />
            </div>
            <div>
              <Modal.Title className="dp-modal-title mb-0">Delivery Staff Profile</Modal.Title>
              <div className="dp-modal-subtitle">
                Rider ID <span className="fw-bold text-primary">#{selectedDeliveryPerson?.delivery_person_id || selectedDeliveryPerson?.user_id}</span>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="dp-modal-body p-4">
          {selectedDeliveryPerson && (
            <div>
              <div className="dp-profile-box p-3 rounded mb-4 d-flex align-items-center gap-3">
                <div className="dp-profile-avatar">
                  {(selectedDeliveryPerson.name || 'D').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h5 className="dp-profile-name mb-1">{selectedDeliveryPerson.name || 'Courier Staff'}</h5>
                  <span className="badge bg-primary">Active Delivery Agent</span>
                </div>
              </div>

              <div className="dp-details-section">
                <div className="dp-section-title mb-3">
                  <FaShieldAlt className="me-2 text-info" /> Contact & Dispatch Details
                </div>

                <div className="dp-detail-grid">
                  <div className="dp-detail-item p-3 rounded mb-2">
                    <span className="dp-item-label">Mobile Contact</span>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="dp-item-val">{selectedDeliveryPerson.phone || selectedDeliveryPerson.mobile || 'N/A'}</span>
                      {(selectedDeliveryPerson.phone || selectedDeliveryPerson.mobile) && (
                        <Button size="sm" variant="outline-light" className="dp-modal-copy-btn" onClick={() => handleCopy(selectedDeliveryPerson.phone || selectedDeliveryPerson.mobile, 'Mobile')}>
                          <FaClipboard /> Copy
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="dp-detail-item p-3 rounded mb-2">
                    <span className="dp-item-label">Email Address</span>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="dp-item-val">{selectedDeliveryPerson.email || 'N/A'}</span>
                      {selectedDeliveryPerson.email && (
                        <Button size="sm" variant="outline-light" className="dp-modal-copy-btn" onClick={() => handleCopy(selectedDeliveryPerson.email, 'Email')}>
                          <FaClipboard /> Copy
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Create Delivery Person Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} scrollable className="dp-modal-modern">
        <Modal.Header closeButton className="dp-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="dp-modal-icon">
              <FaPlus />
            </div>
            <div>
              <Modal.Title className="dp-modal-title mb-0">Register Delivery Staff</Modal.Title>
              <div className="dp-modal-subtitle">Create new courier login credentials</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="dp-modal-body p-4">
          <Form onSubmit={handleCreateDeliveryPerson}>
            <Form.Group className="mb-3">
              <Form.Label className="dp-modal-label">Full Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Rahul Sharma"
                value={newDeliveryPerson.name}
                onChange={(e) => {
                  setNewDeliveryPerson({ ...newDeliveryPerson, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                }}
                className={`dp-modal-input ${formErrors.name ? 'is-invalid' : ''}`}
              />
              {formErrors.name && <div className="text-danger small mt-1">{formErrors.name}</div>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="dp-modal-label">Mobile Phone *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. 9876543210"
                value={newDeliveryPerson.phone}
                onChange={(e) => {
                  setNewDeliveryPerson({ ...newDeliveryPerson, phone: e.target.value });
                  if (formErrors.phone) setFormErrors({ ...formErrors, phone: null });
                }}
                className={`dp-modal-input ${formErrors.phone ? 'is-invalid' : ''}`}
              />
              {formErrors.phone && <div className="text-danger small mt-1">{formErrors.phone}</div>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="dp-modal-label">Email Address *</Form.Label>
              <Form.Control
                type="email"
                placeholder="e.g. rahul@courier.com"
                value={newDeliveryPerson.email}
                onChange={(e) => {
                  setNewDeliveryPerson({ ...newDeliveryPerson, email: e.target.value });
                  if (formErrors.email) setFormErrors({ ...formErrors, email: null });
                }}
                className={`dp-modal-input ${formErrors.email ? 'is-invalid' : ''}`}
              />
              {formErrors.email && <div className="text-danger small mt-1">{formErrors.email}</div>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="dp-modal-label">Password * (Min 6 chars)</Form.Label>
              <Form.Control
                type="password"
                placeholder="••••••••"
                value={newDeliveryPerson.password}
                onChange={(e) => {
                  setNewDeliveryPerson({ ...newDeliveryPerson, password: e.target.value });
                  if (formErrors.password) setFormErrors({ ...formErrors, password: null });
                }}
                className={`dp-modal-input ${formErrors.password ? 'is-invalid' : ''}`}
              />
              {formErrors.password && <div className="text-danger small mt-1">{formErrors.password}</div>}
            </Form.Group>

            <div className="d-flex justify-content-end pt-3 border-top">
              <Button variant="primary" type="submit" disabled={creatingStaff} className="dp-submit-btn fw-bold px-4">
                {creatingStaff ? <><Spinner animation="border" size="sm" className="me-1" /> Creating...</> : 'Register Staff'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AllDeliveryPersons;